import React, { useState, useCallback, useEffect, useRef } from 'react';
import Editor from './components/Editor/Editor';
import AppStatusBar from './components/Layout/AppStatusBar';
import RecoveryBanner from './components/Editor/RecoveryBanner';
import useFileSystem from './hooks/useFileSystem';
import useAutoSave from './hooks/useAutoSave';
import { Analytics } from '@vercel/analytics/react';
import {
  DEFAULT_LINE_STYLES,
  lineStyleToCSSVars,
  mergeLineStyles,
} from './config/lineStyles';
import { buildTranscriptStyles } from './config/transcriptStyles';
import './App.css';

const ZOOM_LEVELS = [75, 100, 125, 150, 175, 200];

// Recursively scan TipTap JSON content for any paragraph whose lineType is one
// of the given types. Used when a file is opened to infer settings it didn't
// save (multilingual for gloss/translation lines, multimodal for embodied).
function contentHasLineTypes(content, types) {
  if (!content || typeof content !== 'object') return false;
  if (types.includes(content.attrs?.lineType)) return true;
  if (Array.isArray(content.content)) {
    return content.content.some((child) => contentHasLineTypes(child, types));
  }
  return false;
}

function App() {
  const editorRef = useRef(null);
  const [status, setStatus] = useState({
    line: 1,
    column: 1,
    charCount: 0,
  });
  const [spellcheckEnabled, setSpellcheckEnabled] = useState(() => {
    return localStorage.getItem('ript-spellcheck') === 'true';
  });
  const [guideEnabled, setGuideEnabled] = useState(() => {
    // On by default for new users; only off if the user has explicitly disabled it.
    return localStorage.getItem('ript-guide') !== 'false';
  });
  const [guideWidth, setGuideWidth] = useState(() => {
    const stored = localStorage.getItem('ript-guide-width');
    return stored ? parseInt(stored, 10) : 70;
  });
  const [zoomLevel, setZoomLevel] = useState(() => {
    const stored = parseInt(localStorage.getItem('ript-zoom'), 10);
    return ZOOM_LEVELS.includes(stored) ? stored : 100;
  });
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('ript-dark-mode') === 'true';
  });
  const [marginWidth, setMarginWidth] = useState(() => {
    const stored = localStorage.getItem('ript-margin-width');
    return stored ? parseInt(stored, 10) : 4;
  });
  const [mode, setMode] = useState(() => {
    // Audible for new users; multimodal only once chosen (or opened from a file)
    const stored = localStorage.getItem('ript-transcription-mode');
    return stored === 'multimodal' ? 'multimodal' : 'audible';
  });
  const [multilingual, setMultilingual] = useState(() => {
    return localStorage.getItem('ript-multilingual') === 'true';
  });
  // Per-line-type styles, loaded from localStorage and merged over the shipped
  // defaults (so a partial or corrupt stored value can't break rendering).
  const [lineStyles, setLineStyles] = useState(() => {
    try {
      const stored = localStorage.getItem('ript-line-styles');
      return stored ? mergeLineStyles(JSON.parse(stored)) : DEFAULT_LINE_STYLES;
    } catch {
      return DEFAULT_LINE_STYLES;
    }
  });

  const handleSetLineStyles = useCallback((next) => {
    const merged = mergeLineStyles(next);
    setLineStyles(merged);
    localStorage.setItem('ript-line-styles', JSON.stringify(merged));
  }, []);

  const handleSetMode = useCallback((nextMode) => {
    const normalized = nextMode === 'audible' ? 'audible' : 'multimodal';
    setMode(normalized);
    localStorage.setItem('ript-transcription-mode', normalized);
  }, []);

  const handleToggleMultilingual = useCallback(() => {
    setMultilingual((prev) => {
      const next = !prev;
      localStorage.setItem('ript-multilingual', String(next));
      return next;
    });
  }, []);

  const handleSetMultilingual = useCallback((value) => {
    const next = !!value;
    setMultilingual(next);
    localStorage.setItem('ript-multilingual', String(next));
  }, []);

  const {
    fileName,
    isModified,
    markModified,
    newFile,
    openFile,
    saveFile,
    saveFileAs,
    exportAsText,
    exportAsWord,
  } = useFileSystem();

  const {
    recovery,
    restoreBackup,
    discardBackup,
    clearBackup,
    triggerBackup,
  } = useAutoSave(editorRef, fileName);

  // Get current editor JSON for saving
  const getEditorJson = useCallback(() => {
    return editorRef.current?.getJSON() ?? null;
  }, []);

  const getEditorText = useCallback(() => {
    return editorRef.current?.getText() ?? '';
  }, []);

  const getEditorDocument = useCallback(() => {
    return editorRef.current?.getDocument() ?? null;
  }, []);

  // File operations
  const handleNew = useCallback(() => {
    newFile();
    editorRef.current?.clearContent();
    clearBackup();
  }, [newFile, clearBackup]);

  const handleOpen = useCallback(async () => {
    const result = await openFile();
    if (!result) return;

    if (result.type === 'ript') {
      editorRef.current?.setContent(result.content);
      // Apply the file's saved mode, if any. Files saved before modes existed
      // have none; switch to multimodal if they contain embodied lines,
      // otherwise leave the current mode.
      if (result.mode === 'audible' || result.mode === 'multimodal') {
        handleSetMode(result.mode);
      } else if (contentHasLineTypes(result.content, ['embodied'])) {
        handleSetMode('multimodal');
      }
      // Apply the file's saved multilingual flag; also force it on if the
      // document actually contains gloss/translation lines, so they render and
      // can be edited correctly regardless of the saved flag.
      const hasMultilingualLines = contentHasLineTypes(result.content, ['gloss', 'translation']);
      if (typeof result.multilingual === 'boolean') {
        handleSetMultilingual(result.multilingual || hasMultilingualLines);
      } else if (hasMultilingualLines) {
        handleSetMultilingual(true);
      }
    } else {
      // Plain text — set as simple text content
      editorRef.current?.setContent(result.content);
    }
    clearBackup();
  }, [openFile, clearBackup, handleSetMode, handleSetMultilingual]);

  const handleSave = useCallback(async () => {
    const json = getEditorJson();
    if (json) {
      const saved = await saveFile(json, mode, multilingual);
      if (saved) clearBackup();
    }
  }, [getEditorJson, saveFile, clearBackup, mode, multilingual]);

  const handleSaveAs = useCallback(async () => {
    const json = getEditorJson();
    if (json) {
      const saved = await saveFileAs(json, mode, multilingual);
      if (saved) clearBackup();
    }
  }, [getEditorJson, saveFileAs, clearBackup, mode, multilingual]);

  const handleExportText = useCallback(async () => {
    const text = getEditorText();
    await exportAsText(text);
  }, [getEditorText, exportAsText]);

  const handleExportWord = useCallback(async () => {
    const doc = getEditorDocument();
    if (doc) await exportAsWord(doc, buildTranscriptStyles(lineStyles, { mode, multilingual }));
  }, [getEditorDocument, exportAsWord, lineStyles, mode, multilingual]);

  // Warn before closing tab with unsaved changes
  useEffect(() => {
    if (!isModified) return;
    const handler = (e) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isModified]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      const next = idx < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[idx + 1] : prev;
      localStorage.setItem('ript-zoom', String(next));
      return next;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      const next = idx > 0 ? ZOOM_LEVELS[idx - 1] : prev;
      localStorage.setItem('ript-zoom', String(next));
      return next;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoomLevel(100);
    localStorage.setItem('ript-zoom', '100');
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 's') {
        e.preventDefault();
        if (e.shiftKey) {
          handleSaveAs();
        } else {
          handleSave();
        }
      } else if (mod && e.key === 'o') {
        e.preventDefault();
        handleOpen();
      } else if (mod && e.key === 'n') {
        e.preventDefault();
        handleNew();
      } else if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        handleZoomIn();
      } else if (mod && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      } else if (mod && e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleNew, handleOpen, handleSave, handleSaveAs, handleZoomIn, handleZoomOut, handleResetZoom]);

  const handleToggleDarkMode = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem('ript-dark-mode', String(next));
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  // Push the active line styles to CSS custom properties on :root so the
  // editor's p.<type>-line rules render them. Same single source feeds export.
  useEffect(() => {
    const root = document.documentElement;
    const vars = lineStyleToCSSVars(lineStyles);
    Object.entries(vars).forEach(([prop, value]) => {
      root.style.setProperty(prop, value);
    });
  }, [lineStyles]);

  const handleToggleSpellcheck = useCallback(() => {
    setSpellcheckEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('ript-spellcheck', String(next));
      return next;
    });
  }, []);

  const handleToggleGuide = useCallback(() => {
    setGuideEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('ript-guide', String(next));
      return next;
    });
  }, []);

  const handleSetGuideWidth = useCallback((width) => {
    const clamped = Math.max(1, Math.min(200, width));
    setGuideWidth(clamped);
    localStorage.setItem('ript-guide-width', String(clamped));
  }, []);

  const handleSetMarginWidth = useCallback((width) => {
    const clamped = Math.max(2, Math.min(10, width));
    setMarginWidth(clamped);
    localStorage.setItem('ript-margin-width', String(clamped));
  }, []);

  const handleContentChange = useCallback(() => {
    markModified();
    triggerBackup();
  }, [markModified, triggerBackup]);

  const handleRestore = useCallback(() => {
    const content = restoreBackup();
    if (content) {
      editorRef.current?.setContent(content);
      markModified();
    }
  }, [restoreBackup, markModified]);

  const handleStatusChange = useCallback((newStatus) => {
    setStatus(newStatus);
  }, []);

  return (
    <div className="app">
      <main className="editor-container">
        <Editor
          ref={editorRef}
          onContentChange={handleContentChange}
          onStatusChange={handleStatusChange}
          spellcheckEnabled={spellcheckEnabled}
          recoveryBanner={
            recovery ? (
              <RecoveryBanner
                recovery={recovery}
                onRestore={handleRestore}
                onDiscard={discardBackup}
              />
            ) : null
          }
          fileActions={{
            onNew: handleNew,
            onOpen: handleOpen,
            onSave: handleSave,
            onSaveAs: handleSaveAs,
            onExportText: handleExportText,
            onExportWord: handleExportWord,
          }}
          optionsActions={{
            spellcheckEnabled,
            onToggleSpellcheck: handleToggleSpellcheck,
            guideEnabled,
            guideWidth,
            onToggleGuide: handleToggleGuide,
            onSetGuideWidth: handleSetGuideWidth,
            zoomLevel,
            onZoomIn: handleZoomIn,
            onZoomOut: handleZoomOut,
            onResetZoom: handleResetZoom,
            darkMode,
            onToggleDarkMode: handleToggleDarkMode,
            marginWidth,
            onSetMarginWidth: handleSetMarginWidth,
            mode,
            onSetMode: handleSetMode,
            multilingual,
            onToggleMultilingual: handleToggleMultilingual,
            lineStyles,
            onSetLineStyles: handleSetLineStyles,
          }}
        />
      </main>
      <AppStatusBar
        line={status.line}
        column={status.column}
        charCount={status.charCount}
        fileName={fileName}
        isModified={isModified}
        lineType={status.lineType}
        zoomLevel={zoomLevel}
      />
      <Analytics />
    </div>
  );
}

export default App;
